import { CssRules } from '../features/forms/tabs/design/design.component';
import { LabelInfo } from './label-info.model';
import { Partner } from './partner.model';
import { DictionaryLabelInfo } from './dictionary-label-info';
import { ApplicationMappings } from './application-type.model';
import { FluxHelper } from 'src/app/features/forms/tabs/flux/helpers/flux-helper';
import {
  PresentationControl,
  SectionData,
  StepData,
} from 'src/app/features/forms/tabs/flux/flux.model';

export class NotificationConfig {
  enabled: boolean = false;
  titleTemplate: string = 'Status update: {statusName}';
  messageTemplate: string = 'Your application status has been updated to: {statusName}';
}

export class InheritedField {
  id: string = '';
  fieldName: string = '';
}

export class ParentChildConfig {
  parentApplicationUuid: string | null = null;
  inheritedFields: InheritedField[] = [];
  notificationConfig: NotificationConfig = new NotificationConfig();
}

export class ContactMethodConfig {
  emailFieldId: string | null = null;
  phoneFieldId: string | null = null;
  preferredMethod: 'email' | 'phone' | 'both' = 'email';
  continuationBaseUrl: string = '';
}

export class FormModel {
  applicationId: number;
  applicationUuid: string;
  generalData: GeneralDataModel;
  design: DesignModel;
  flux: StepData[];
  api: ApiData;
  partners: AppPartner[];
  documents: AppDocument[];
  statuses: StatusModel[];
  display: DisplayModel;
  generatedCss: CssRules;
  metadataFields: [];
  errors: { [tabName: string]: string[] };
  applicationMappings: ApplicationMappings;
  parentChildConfig?: ParentChildConfig;
  contactMethodConfig?: ContactMethodConfig;
}

export class AppDataControl {
  controlId: string;
  fillValue: string;
  fillValueId: string;
  fillValues?: string[];
  fillValueIds?: string[];

  constructor(
    controlId: string = '',
    fillValue: string = '',
    fillValueId: string = '',
    fillValues?: string[],
    fillValueIds?: string[],
  ) {
    this.controlId = controlId || '';
    this.fillValue = fillValue || '';
    this.fillValueId = fillValueId || '';
    this.fillValues = fillValues;
    this.fillValueIds = fillValueIds;
  }
}

export class AppDataSection {
  sectionId: string;
  controls: AppDataControl[];

  constructor(sectionId: string = '', controls: AppDataControl[] = []) {
    this.sectionId = sectionId || '';
    this.controls = controls || [];
  }
}

export class AppDataStep {
  stepId: string;
  type: number;
  sections: AppDataSection[];

  constructor(
    stepId: string = '',
    type: number,
    sections: AppDataSection[] = [],
  ) {
    this.stepId = stepId || '';
    this.type = type;
    this.sections = sections || [];
  }
}

export class AppDataFill {
  metadata: {
    appDataId: string;
    configurationId: string;
    currentStepId: string;
    statusId: string;
    language: string;
    offerId: string;
    isSubmitted: boolean;
  };
  flux: AppDataStep[];
  inheritedFields?: Record<string, any>;

  constructor(
    appDataId: string = '',
    configurationId: string = '',
    currentStepId: string = '',
    statusId: string = '',
    language: string = '',
    flux: AppDataStep[] = [],
    offerId: string = '',
    isSubmitted: boolean = false,
  ) {
    this.metadata = {
      appDataId: appDataId || '',
      configurationId: configurationId || '',
      currentStepId: currentStepId || '',
      statusId: statusId || '',
      language: language || '',
      offerId: offerId || '',
      isSubmitted: isSubmitted || false,
    };
    this.flux = flux || [];
  }
}

export class AppData {
  id?: number;
  appDataId: string;
  appConfigurationId: number;
  fillData?: AppDataFill;
  initFlowValues?: string;
  withNextStep: boolean;
  blockPreviousStep: boolean;
  env: string;
  status: AppDataStatus;

  constructor(data: {
    id?: number;
    appDataId: string;
    appConfigurationId: number;
    fillData?: AppDataFill;
    initFlowValues?: string;
    withNextStep: boolean;
    blockPreviousStep: boolean;
    env: string;
  }) {
    ((this.id = data.id ?? undefined), (this.appDataId = data.appDataId || ''));
    this.appConfigurationId = data.appConfigurationId || 0;
    this.fillData = data.fillData;
    this.initFlowValues = data.initFlowValues;
    this.withNextStep = data.withNextStep;
    this.blockPreviousStep = data.blockPreviousStep;
    if (!data.env) {
      this.env = 'Config';
    } else {
      this.env = data.env;
    }
  }
}

export class FormModelExtenxions {
  formModel: FormModel;

  constructor(model: FormModel) {
    this.formModel = model;
  }

  private addInitFlowValuesItems(items: MethodField[]): void {
    const initFlowValuesRaw = (this.formModel as any)?.generalData
      ?.initFlowValues;
    if (initFlowValuesRaw === null || initFlowValuesRaw === undefined) {
      return;
    }

    let parsed: any = undefined;
    if (typeof initFlowValuesRaw === 'string') {
      const trimmed = initFlowValuesRaw.trim();
      if (!trimmed) {
        return;
      }
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        // If config contains invalid JSON, do not expose any initFlowValues fields.
        return;
      }
    } else {
      parsed = initFlowValuesRaw;
    }

    const existingIds = new Set(items.map((i) => i.id));
    const out: MethodField[] = [];

    const walk = (node: any, pathParts: string[]) => {
      if (node === null || node === undefined) {
        // Leaf
        if (pathParts.length > 0) {
          out.push(this.buildInitFlowField(pathParts));
        }
        return;
      }

      if (Array.isArray(node)) {
        node.forEach((child, idx) => walk(child, [...pathParts, String(idx)]));
        return;
      }

      if (typeof node === 'object') {
        Object.keys(node).forEach((key) =>
          walk(node[key], [...pathParts, key]),
        );
        return;
      }

      // Primitive leaf
      if (pathParts.length > 0) {
        out.push(this.buildInitFlowField(pathParts));
      }
    };

    walk(parsed, []);

    for (const f of out) {
      if (!existingIds.has(f.id)) {
        items.push(f);
        existingIds.add(f.id);
      }
    }
  }

  private buildInitFlowField(pathParts: string[]): MethodField {
    const underscorePath = pathParts.join('_');
    return {
      fieldName: `initFlowValues_${underscorePath}`,
      fieldType: StepControlType.Metadata,
      id: `initFlowValues_${underscorePath}`,
    };
  }

  public fluxItems(
    untilStepIdentifier: string | undefined = undefined,
  ): MethodField[] {
    let items: MethodField[] = [];

    if (this.formModel.metadataFields) {
      this.formModel.metadataFields.forEach((fieldValue) => {
        items.push({
          fieldName: fieldValue,
          fieldType: StepControlType.Metadata,
          id: `metadata.${fieldValue}`,
        });
      });
    }

    this.addInitFlowValuesItems(items);

    if (!this.formModel?.flux) {
      return items;
    }

    if (this.formModel.documents?.length > 0) {
      items = items.concat(this.extractDocumentsFluxItems(this.formModel.flux));
    }
    items = items.concat(
      this.extractSectionsFluxItems(this.formModel.flux, untilStepIdentifier),
    );

    return items;
  }
  private extractSectionsFluxItems(
    steps: StepData[],
    untilStepIdentifier: string | undefined = undefined,
  ): MethodField[] {
    let items: MethodField[] = [];

    for (let step of steps) {
      if (step.sections) {
        if (untilStepIdentifier && step.identifier === untilStepIdentifier) {
          break;
        }
        step.sections.forEach((section) => {
          if (section.controls) {
            section.controls.forEach((control) => {
              if (
                [
                  StepControlType.Buttons,
                  StepControlType.NewRow,
                  StepControlType.FluxInfo,
                  StepControlType.Paragraph,
                ].includes(control.type) == false
              ) {
                items.push({
                  fieldName: `${step.name} -> ${section.title} -> ${control.title}`,
                  fieldType: control.type,
                  manulInputControlType: control.specificControlData?.type,
                  id: control.identifier,
                });
              }
            });
          }
        });
      }
    }

    return items;
  }

  private extractDocumentsFluxItems(stepData: StepData[]): MethodField[] {
    let items: MethodField[] = [];

    stepData?.forEach((step) => {
      if (step.type === FluxType.UploadDocument) {
        step.config?.documents?.forEach(
          (document: { documentId: string; name: string }) => {
            items.push({
              fieldName: `${step.name} -> ${
                FluxHelper.getDocumentNameById(
                  document?.documentId,
                  this.formModel,
                ) || document.name
              }`,
              fieldType: step.type,
              id: document?.documentId
                ? `upload.${document.documentId}`
                : `${step.type}.${step.name}`,
            });
          },
        );
      } else if (step.type === FluxType.GenerateDocument) {
        step.config?.generatedDocuments?.forEach(
          (document: { id: string; name: string }) => {
            items.push({
              fieldName: `${step.name} -> ${
                FluxHelper.getDocumentNameById(document?.id, this.formModel) ||
                document.name
              }`,
              fieldType: step.type,
              id: document?.id
                ? `generate.${document.id}`
                : `${step.type}.${step.name}`,
            });
          },
        );
      } else if (step.type === FluxType.Sign) {
        if (step.config?.documents) {
          step.config?.documents.forEach(
            (document: { id: string; name: string }) => {
              items.push({
                fieldName: `${step.name} -> ${
                  FluxHelper.getDocumentNameById(
                    document?.id,
                    this.formModel,
                  ) || document.name
                }`,
                fieldType: step.type,
                id: document?.id
                  ? `sign.${document.id}`
                  : `${step.type}.${step.name}`,
              });
            },
          );
        }

        if (step.config?.systemDocuments) {
          step.config?.systemDocuments.forEach(
            (document: { id: string; name: string }) => {
              items.push({
                fieldName: `${step.name} -> ${
                  FluxHelper.getDocumentNameById(
                    document?.id,
                    this.formModel,
                  ) || document.name
                }`,
                fieldType: step.type,
                id: document?.id
                  ? `system.${document.id}`
                  : `${step.type}.${step.name}`,
              });
            },
          );
        }
      }
    });
    return items;
  }

  /**
   * Returns inherited parent fields as MethodField items.
   * Reusable across all system step settings components.
   */
  public static getInheritedMethodFields(formModel: FormModel): MethodField[] {
    const result: MethodField[] = [];
    const inheritedFields = formModel.parentChildConfig?.inheritedFields;
    if (inheritedFields?.length) {
      inheritedFields.forEach((field) => {
        result.push({
          fieldName: `Inherited -> ${field.fieldName}`,
          fieldType: 'inherited' as any,
          id: field.id,
        });
      });
    }
    return result;
  }

  /**
   * Returns flux items + inherited parent fields (no API methods).
   * Use this in system step settings that need parent field references.
   */
  public fluxItemsWithInherited(
    untilStepIdentifier: string | undefined = undefined,
  ): MethodField[] {
    const items = this.fluxItems(untilStepIdentifier);
    items.push(...FormModelExtenxions.getInheritedMethodFields(this.formModel));
    return items;
  }

  public fluxItemsWithGetApiMethods(
    excludedMethodId: string = '',
  ): MethodField[] {
    let items = this.fluxItemsWithInherited();
    this.formModel.api.getItems.forEach((apiMethod) => {
      if (apiMethod.id !== excludedMethodId) {
        apiMethod.fields?.forEach((f) => {
          items.push({
            fieldName: `${apiMethod.method} -> ${f.fieldName}`,
            fieldType: StepControlType.ApiMethodField,
            id: f.id,
          });
        });
      }
    });
    this.formModel.api.validationItems.forEach((validateApiMethod) => {
      validateApiMethod.fields?.forEach((f) => {
        items.push({
          fieldName: `${validateApiMethod.method} -> ${f.fieldName}`,
          fieldType: StepControlType.ApiMethodField,
          id: f.id,
        });
      });
    });

    return items;
  }

  public offerMethodsItems(): MethodField[] {
    let items: MethodField[] = [];

    let offerMethodsIds: string[] = [];
    this.formModel.partners?.forEach((p) => {
      p.generalSettings.offers?.forEach((o) => {
        offerMethodsIds.push(o.apiMethodId);
      });
    });

    offerMethodsIds = [...new Set(offerMethodsIds)];

    const allApiItems = [
      ...this.formModel.api.getItems,
      ...this.formModel.api.validationItems,
      ...this.formModel.api.sendItems,
    ];

    offerMethodsIds.forEach((methodId) => {
      const apiMethod = allApiItems.find((item) => item.id === methodId);
      if (apiMethod) {
        apiMethod.fields?.forEach((f) => {
          items.push({
            fieldName: `${apiMethod.method} -> ${f.fieldName}`,
            fieldType: FluxType.ViewOffer,
            id: `api_${apiMethod.id} -> ${f.id}`,
          });
        });
      }
    });

    return items;
  }
}

export class ApiData {
  getItems: GetApiItem[];
  validationItems: ValidateApiItem[];
  sendItems: ApiItem[];
}

export class ApiItem {
  id: string;
  method: string;
  fields: MethodField[];
  type: ApiMethodType;
}

export class GetApiItem {
  id: string;
  method: string;
  fields: GetMethodField[];
  type: ApiMethodType;
}
export class ValidateApiItem {
  id: string;
  method: string;
  fields: MethodField[];
  type: ApiMethodType;
}

export class DesignModel {
  fonts: FontConfig[] = [];
  buttons: ButtonConfig[] = [];
  otpActionButtons: ButtonConfig[] = [];
  components: ComponentConfig[] = [];
  tables: TableConfig[] = [];
  backgroundColor: string;
  fontSize: string;
  buttonsHorizontalPosition: string;
  buttonsVerticalPosition: string;
  maxWidth: string;
  marginLeft: string;
  marginRight: string;
  marginTop: string;
  marginBottom: string;

  constructor(data?: any) {
    if (data) {
      if (data.fonts) {
        this.fonts = data.fonts.map((font: any) => new FontConfig(font));
      }

      if (data.buttons) {
        this.buttons = data.buttons.map(
          (button: any) => new ButtonConfig(button),
        );
      }

      if (data.components) {
        this.components = data.components.map(
          (component: any) => new ComponentConfig(component),
        );
      }

      if (data.tables) {
        this.tables = data.tables.map((table: any) => new TableConfig(table));
      }

      this.backgroundColor = data.backgroundColor;
      this.fontSize = data.fontSize;
      this.buttonsHorizontalPosition = data.horizontalPosition;
      this.buttonsVerticalPosition = data.verticalPosition;
      this.maxWidth = data.maxWidth;
    }
  }
}

export class FontConfig {
  name: string;
  fontFamily: string;
  fontSize: string;
  color: string;
  fontWeight: string;
  textAlign: string;
  letterSpacing: string;

  constructor(data?: any) {
    if (data) {
      this.name = data.name;
      this.fontFamily = data.fontFamily;
      this.fontSize = data.fontSize;
      this.color = data.color;
      this.fontWeight = data.fontWeight;
      this.textAlign = data.textAlign;
      this.letterSpacing = data.letterSpacing;
    }
  }
}

export class ButtonConfig {
  name: string;
  backgroundColor: string;
  color: string;
  border: string;
  borderColor: string;
  borderRadius: string;
  horizontalPadding: string;
  verticalPadding: string;
  fontFamily: string;
  fontSize: string;
  letterSpacing: string;
  width: string;
  height: string;
  minWidth: string;
  minHeight: string;
  maxWidth: string;
  maxHeight: string;
  horizontalPosition?: string;

  constructor(data?: any) {
    if (data) {
      this.name = data.name;
      this.backgroundColor = data.backgroundColor;
      this.color = data.color;
      this.border = data.border;
      this.borderColor = data.borderColor;
      this.borderRadius = data.borderRadius;
      this.horizontalPadding = data.horizontalPadding;
      this.verticalPadding = data.verticalPadding;
      this.fontFamily = data.fontFamily;
      this.fontSize = data.fontSize;
      this.letterSpacing = data.letterSpacing;
      this.width = data.width;
      this.height = data.height;
      this.minWidth = data.minWidth;
      this.minHeight = data.minHeight;
      this.maxWidth = data.maxWidth;
      this.maxHeight = data.maxHeight;
      this.horizontalPosition = data.horizontalPosition;
    }
  }
}

export class TableConfig {
  headerConfig: HeaderConfig;
  detailConfig: DetailConfig;

  constructor(table?: any) {
    if (table) {
      this.headerConfig = table.headerConfig;
      this.detailConfig = table.detailConfig;
    }
  }
}

export class HeaderConfig {
  name: string;
  backgroundColor: string;
  textColor: string;
  border: string;
  borderColor: string;
  textAlign: string;
  fontFamily: string;
  fontSize: string;
  letterSpacing: string;
  padding: string;
  borderCollapse: string;
  borderSpacing: string;
  verticalAlign: string;
  width: string;
  height: string;

  constructor(data?: any) {
    if (data) {
      this.name = data.name;
      this.backgroundColor = data.backgroundColor;
      this.textColor = data.color;
      this.border = data.border;
      this.textAlign = data.textAlign;
      this.fontFamily = data.fontFamily;
      this.fontSize = data.fontSize;
      this.letterSpacing = data.letterSpacing;
      this.padding = data.padding;
      this.borderCollapse = data.borderCollapse;
      this.borderSpacing = data.borderSpacing;
      this.verticalAlign = data.verticalAlign;
      this.width = data.width;
      this.height = data.height;
    }
  }
}

export class DetailConfig {
  name: string;
  backgroundColor: string;
  textColor: string;
  textAlign: string;
  letterSpacing: string;
  padding: string;
  rowBorder: string;
  rowBorderColor: string;
  hoverBackgroundColor: string;
  verticalAlign: string;

  constructor(data?: any) {
    if (data) {
      this.name = data.name;
      this.backgroundColor = data.backgroundColor;
      this.textColor = data.color;
      this.textAlign = data.textAlign;
      this.letterSpacing = data.letterSpacing;
      this.padding = data.padding;
      this.rowBorder = data.rowBorder;
      this.hoverBackgroundColor = data.hoverBackgroundColor;
      this.verticalAlign = data.verticalAlign;
    }
  }
}

export class ComponentConfig {
  name: string;
  backgroundColor: string;
  border: string;
  borderRadius: string;
  labelStyle: string;
  textAlign: string;
  color?: string | undefined;
  borderColor?: string | undefined;
  weight?: string | undefined;
  valueStyle?: string;
  startColor?: string;
  endColor?: string;
  height?: string;
  textareaMaxHeight?: string;
  marginTop?: string;
  marginBottom?: string;

  constructor(data?: any) {
    if (data) {
      this.name = data.name;
      this.backgroundColor = data.backgroundColor;
      this.border = data.border;
      this.borderRadius = data.borderRadius;
      this.labelStyle = data.labelStyle;
      this.valueStyle = data.valueStyle;
      this.textAlign = data.textAlign;
      this.color = data.color;
      this.weight = data.weight;
      this.startColor = data.startColor;
      this.endColor = data.endColor;
      this.height = data.height;
      this.textareaMaxHeight = data.textareaMaxHeight;
      this.marginTop = data.marginTop;
      this.marginBottom = data.marginBottom;
    }
  }
}
export class DisplayModel {
  sections: SectionData[];
}

export class GeneralDataModel {
  name: string;
  applicationType: number;
  languages: string[];
  defaultLanguage: string;
  continueWithOffer: boolean;
  showCounterStep: boolean;
  showNameStep: boolean;
  showAllSteps: boolean;
  showProgressBar: boolean;
  initFlowValues?: string;
}

export interface FormsState {
  data: FormModel;
  selectedTab: string;
  version: string;
  canGetFromProd: boolean;
  canGetFromTest: boolean;
  canPublishInProd: boolean;
  canPublishInTest: boolean;
  applicationUuid: string;
}

export const initialFormulareState: FormsState = {
  data: {
    applicationId: 0,
    applicationUuid: '',
    generalData: {
      name: '',
      applicationType: 0,
      languages: [],
      defaultLanguage: '',
      continueWithOffer: false,
      showCounterStep: false,
      showNameStep: false,
      showAllSteps: false,
      showProgressBar: false,
      initFlowValues: '',
    },
    design: {
      fonts: [],
      buttons: [],
      otpActionButtons: [],
      components: [],
      tables: [],
      backgroundColor: '',
      fontSize: '',
      buttonsHorizontalPosition: '',
      buttonsVerticalPosition: '',
      maxWidth: '',
      marginLeft: '',
      marginRight: '',
      marginTop: '',
      marginBottom: '',
    },
    flux: [],
    api: { getItems: [], validationItems: [], sendItems: [] },
    partners: [],
    documents: [],
    statuses: [],
    display: {
      sections: [],
    },
    generatedCss: {},
    metadataFields: [],
    errors: {},
    applicationMappings: {
      applicationTypeId: 0,
      columnMappings: [],
    },
    parentChildConfig: {
      parentApplicationUuid: null,
      inheritedFields: [],
      notificationConfig: {
        enabled: false,
        titleTemplate: 'Status update: {statusName}',
        messageTemplate: 'Your application status has been updated to: {statusName}',
      },
    },
  },
  selectedTab: 'Date generale',
  canGetFromProd: false,
  canGetFromTest: false,
  canPublishInProd: false,
  canPublishInTest: false,
  applicationUuid: '',
  version: '',
};

export class PartnerDictionaryMapping {
  dictionaryId: string;
  dictionaryName: string;
  mappings: PartnerKeyValueItem[];
}

export class PartnerSettings {
  partnerMethodsApi: PartnerMethodApi[] = [];
}

export class PartnerMethodApi {
  methodId: string;
  methodType: string;
  callType: string;
  bodyConfiguration: PartnerMethodBody;
  responseConfiguration: PartnerMethodResponse;
  urlConfiguration: PartnerMethodUrl;
  authValue: PartnerMethodApiAuth;
  headers: PartnerKeyValueItem[];
  // INBOUND-specific properties
  inboundMethodType?: InboundMethodType; // Type of INBOUND method
}

export enum InboundMethodType {
  Standard = 'Standard',
  StatusMethod = 'StatusMethod',
}

export class PartnerMethodApiAuth {
  headerName: string | null;
  apiKey: string | null;
  authType: string;
}

export class PartnerKeyValueItem {
  key: string;
  keyName: string | null;
  value: string;
}
export class PartnerStatusMapping {
  partnerStatus: string;
  appStatus: string;
}

export class PartnerMethodUrl {
  urlValue: string;
  timeout: number;
  testUrl: string;
  prodUrl: string;
  parameterMappings: UrlMapFields[];
}

export class PartnerMethodResponse {
  contentType: string;
  jsonValue: string;
  responseMapFields: FieldsMapping[];
}

export class UrlMapFields {
  urlParameter: string;
  fluxField: string;
  fluxFieldId: string;
  fieldType: string;
}

export class PartnerMethodBody {
  contentType: string;
  jsonValue: string;
  bodyMapFields: FieldsMapping[];
}

export class FieldsMapping {
  partnerField: string;
  fluxFieldId: string;
  methodFieldId: string;
  fieldType: string;
}

export class MethodField {
  id: string;
  fieldName: string;
  fieldType: StepControlType | FluxType | string;
  manulInputControlType?: ManualInputControlType | undefined = undefined;
  fieldValue?: string | undefined = undefined;
  expectedValue?: string | undefined = undefined;
}

export class GetMethodField {
  id: string;
  fieldName: string;
  fieldType: StepControlType | FluxType | string;
  documentId: string;
}

export class ManualInputControlData {
  type: ManualInputControlType;
  isSimpleValidation: boolean;
  isApiValidation: boolean;
  apiValidationMethod?: string | null;
  apiValidationPartner?: string | null;
  allowedCharacters?: string;
  maxCharacters?: number;
  minValue?: number;
  maxValue?: number;
  allowOnlyPast?: boolean;
  allowOnlyFuture?: boolean;
  isMandatory: boolean;
  isPrecompletable?: boolean;
  precompletionFieldId?: string | null;
}

export class ParagraphControlData {
  descriptionTranslations: LabelInfo[];
  isMandatory: boolean;
  isMandatoryTranslations?: LabelInfo[];
}

export class DictionaryControlData {
  dictionaryUUID: string;
  showAsRadioButtonsList: boolean;
  relatedDictionariesTranslations: DictionaryLabelInfo[];
  dictionaryDefaultValueId?: number | null;
  selectedRelatedDictionaries: string[];
  allowMultipleSelection?: boolean;
  isMandatory?: boolean;
}

export enum FluxType {
  Kyc = 0,
  Email = 1,
  Phone = 2,
  Sign = 3,
  Status = 4,
  Custom = 5,
  Pause = 6,
  IdScan = 7,
  GenerateDocument = 8,
  Action = 9,
  UploadDocument = 10,
  DownloadDocument = 11,
  ViewOffer = 12,
  Final = 13,
  ViewDocument = 14,
  NETOPIA = 15,
  EUPLATESC = 16,
  OpenBanking = 17,
  Validation = 18,
  OmniCheckPerson = 19,
  OmniCheckBusiness = 20,
}

export enum SectionType {
  Presentation,
  Collect,
  Display,
}

export enum StepControlType {
  ManualInput = 0,
  Paragraph = 1,
  CheckBox = 2,
  Dictionary = 3,
  Buttons = 4,
  FluxInfo = 5,
  NewRow = 6,
  Metadata = 7,
  ApiMethodField = 8,
  DownloadDocuments = 9,
  ViewDocuments = 10,
  UploadDocuments = 11,
}

export enum DimensionType {
  Percentage,
  Pixels,
}

export enum ManualInputControlType {
  Text,
  Numeric,
  DateTime,
  Email,
  Phone,
  TextArea,
}

export enum SignatureType {
  Electronic,
  Handwritten,
  Qualified,
}

export enum ApiMethodType {
  GetData,
  SendData,
  Validation,
}

export class StatusModel {
  id: string;
  name: string;
  order: number;
  final: boolean;
  isActive: boolean;
  actions: StatusAction[] = [];
  childApplicationUuids: string[] = [];
  sendNotifications: boolean = false;

  constructor(
    id: string,
    denumire: string,
    ordine: number,
    final: boolean,
    isActive: boolean,
    actions: StatusAction[],
    childApplicationUuids: string[] = [],
    sendNotifications: boolean = false,
  ) {
    this.id = id;
    this.name = denumire;
    this.order = ordine;
    this.final = final;
    this.isActive = isActive;
    this.actions = actions;
    this.childApplicationUuids = childApplicationUuids;
    this.sendNotifications = sendNotifications;
  }
}

export class StatusAction {
  id?: string;
  actionTitle: string;
  apiMethodId: string;
  partnerIds: number[] = [];
}

export class PresentationParagraphControl extends PresentationControl {
  descriptionTranslations: LabelInfo[];
}

export class AppPartner extends Partner {
  documents?: AppPartnerDocuments[] = [];
  generalSettings: AppPartnerGeneralSettings;
  partnerSettings: PartnerSettings;
  partnerDictionaryMapping: PartnerDictionaryMapping[];
  partnerStatusMapping: PartnerStatusMapping[];
}

export class AppPartnerGeneralSettings {
  acceptMultipleOffers: boolean;
  offers: AppPartnerGeneralSettingsOffer[] = [];
  receivedOffers: AppPartnerGeneralSettingsReceivedOffer[] = [];
  valability: number;
  hasCustomValability: boolean;
  statusWorkerSettings: AppStatusWorkerSettings;
}
export class AppStatusWorkerSettings {
  maxRetries: number;
  loopInterval: number;
}
export class AppPartnerGeneralSettingsOffer {
  id: string;
  name: string;
  apiMethodId: string;
  identifier: string;
  value: string;
  customValability?: number;
  apiMethodFields?: any[] = [];
}

export class AppPartnerGeneralSettingsReceivedOffer {
  id: string;
  offers: AppPartnerGeneralSettingsOffer[];
}

export class AppPartnerDocuments {
  id: string;
  documentId: string;
  documentName: string;
  apiMethodId: string;
  isEditMode: boolean;
}

export class AppDocument {
  id: string;
  name: string;
  translations: LabelInfo[];
  generatedByApp: boolean;
  isStatic: boolean;
  isActive: boolean;
  versions: AppDocumentVersion[] = [];
}
export class SignedDocument extends AppDocument {
  sourceDocuments: string[];
}

export interface AppDocumentExtended extends AppDocument {
  isApiDocument?: boolean;
}

export class AppDocumentVersion {
  id: number;
  applicationId: number;
  name: string;
  documentId: string;
  version: number;
  validFrom: Date;
  validTo: Date | undefined;
  isEditMode: boolean;
  data: string | null;
  fluxBookmarks: BookmarkMapping[];
  isTemporary: boolean;
}

export class BookmarkMapping {
  bookmark: string;
  controlId: string;
}

export class ErrorTranslations {
  code: string;
  translations: LabelInfo[];
}

export class UploadDocument {
  documentId: string;
  fileName: string;
  fileData: string | undefined;
}

export class UploadDocumentDetails extends UploadDocument {
  documentName: string;
  documentExtensions: string;
  documentMaxSize: number;
  isMandatory: boolean;
  isUploading: boolean;
  isUploadSuccess: boolean;
}

export class StepsSettings {
  showCounter: boolean;
  showLabels: boolean;
  showAllSteps: boolean;
  showProgressBar: boolean;
}

export enum AppDataStatus {
  new = 0,
  finished = 1,
}
